<template>
  <v-dialog
    :value="isPausaDialogOpen"
    width="800px"
    persistent
  >
    <v-card class="pa-sm-10 pa-3">
      <btn-close-dialog
        v-if="!bloquearTela"
        :handle-click="closeDialog"
      />

      <v-card-title class="justify-center text-h5">
        {{ !pausaEstaAtiva ? 'Escolher pausa' : 'Sua pausa' }}
      </v-card-title>

      <v-scroll-y-transition mode="out-in">
        <v-card-text
          v-if="!pausaEstaAtiva"
          class="mt-2"
        >
          <v-form
            ref="formRef"
            class="multi-col-validation"
            @submit.prevent="handleOnSubmitBtnClick"
          >
            <v-select
              v-model="pausaSelecionada"
              class="mb-6"
              dense
              outlined
              label="Selecionar pausa"
              validate-on-blur
              item-value="id"
              item-text="nome"
              return-object
              no-data-text="Nenhuma pausa disponível"
              :items="pausas"
              :rules="[validators.select]"
            />

            <v-col
              cols="12"
              class="d-flex justify-center"
            >
              <v-btn
                class="me-3 mb-1"
                color="primary"
                type="submit"
                :loading="loading"
              >
                Confirmar
              </v-btn>

              <v-btn
                class="mb-1"
                color="secondary"
                outlined
                @click.prevent="closeDialog"
              >
                Cancelar
              </v-btn>
            </v-col>
          </v-form>
        </v-card-text>

        <v-card-text
          v-else
          class="mt-2"
        >
          <v-text-field
            v-model="pausaNome"
            class="mt-2"
            outlined
            label="Nome da pausa"
            readonly
          />

          <span
            class="text-4xl font-weight-bold d-flex justify-center mb-5"
            :class="isAgenteAtrasado ? 'error--text' : 'text--primary'"
          >
            {{ horarioFormatado }}
          </span>

          <v-col
            cols="12"
            class="d-flex justify-center"
          >
            <v-alert
              class="mt-4"
              dense
              shaped
              outlined
              type="warning"
            >
              {{ bloquearTela?mensagemTelaBloqueada:mensagemTelaNaoBloqueada }}
            </v-alert>
          </v-col>

          <v-col
            class="d-flex justify-center"
            cols="12"
          >
            <v-btn
              class="me-3 mb-1"
              color="primary"
              :loading="loading"
              @click.prevent="finalizarPausa"
            >
              Finalizar
            </v-btn>
          </v-col>
        </v-card-text>
      </v-scroll-y-transition>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from 'vue'
import { ref, computed, watch, onMounted } from '@vue/composition-api'
import { select, required } from '@core/utils/validation'
import { storeToRefs } from 'pinia'
import BtnCloseDialog from '@/components/btn-close-dialog/BtnCloseDialog.vue'
import PausasService from '@/services/modules/PausasService'
import usePausaStore from '@/store/pausas'
import useDialog from '@/composables/useDialog'
import { EventBus } from '@/utils/EventBus'
import { getDateTimeAtualNoFormatoIsoDateTimeString } from '@/utils/dateTime'
import { getDiferencaEmSegundosParaADataAtual } from '@/utils/dateDifference'

export default {
  components: {
    BtnCloseDialog,
  },

  emits: ['is-pausa-dialog-open'],

  props: {
    isPausaDialogOpen: {
      type: Boolean,
      required: true,
    },
  },

  setup(props, { emit }) {
    const { formRef, loading, closeDialog } = useDialog(emit, 'is-pausa-dialog-open')

    const pausaStore = usePausaStore()
    const {
      id: pausaId,
      nome: pausaNome,
      bloquearTela,
      horarioMarcacao,
      timer,
      timerInterval,
    } = storeToRefs(pausaStore)

    const mensagemTelaBloqueada = 'Você entrou em pausa e não pode navegar pela plataforma.'
    const mensagemTelaNaoBloqueada = 'Você entrou em pausa, mas ainda pode navegar pela plataforma.'

    const isAgenteEmPausa = computed(() => !!pausaId.value)
    const pausas = ref([])
    const pausaSelecionada = ref(null)
    const isAgenteAtrasado = ref(false)
    const pausaEstaAtiva = ref(false)

    const horarioFormatado = computed(() => {
      const totalDeSegundos = timer.value
      const dias = Math.floor(totalDeSegundos / (24 * 3600))
      const horas = Math.floor((totalDeSegundos % (24 * 3600)) / 3600)
      const minutos = Math.floor((totalDeSegundos % 3600) / 60)
      const segundos = totalDeSegundos % 60

      const partes = []

      if (dias > 0) {
        partes.push(`${dias}d`)
      }
      if (horas > 0 || dias > 0) {
        partes.push(`${horas.toString().padStart(2, '0')}h`)
      }

      partes.push(`${minutos.toString().padStart(2, '0')}m`)
      partes.push(`${segundos.toString().padStart(2, '0')}s`)

      return partes.join(' ')
    })

    function iniciarTimer() {
      pausaEstaAtiva.value = true

      pausaStore.setId(pausaSelecionada.value.id)
      pausaStore.setNome(pausaSelecionada.value.nome)
      pausaStore.setTempo(pausaSelecionada.value.tempo)
      pausaStore.setBloquearTela(pausaSelecionada.value.bloquearTela)
      pausaStore.setHorarioMarcacao(getDateTimeAtualNoFormatoIsoDateTimeString())
      pausaStore.setTimer(getDiferencaEmSegundosParaADataAtual(horarioMarcacao.value))

      EventBus.$emit('pausa-iniciada')

      const tempoLimite = pausaSelecionada.value.tempo * 60

      pausaStore.setTimerInterval(setInterval(() => {
        pausaStore.setTimer(getDiferencaEmSegundosParaADataAtual(horarioMarcacao.value))

        if (tempoLimite && timer.value >= tempoLimite) {
          isAgenteAtrasado.value = true
        }
      }, 1000))
    }

    function continuarTimer() {
      pausaEstaAtiva.value = true

      pausaStore.setId(pausaSelecionada.value.id)
      pausaStore.setNome(pausaSelecionada.value.nome)
      pausaStore.setTempo(pausaSelecionada.value.tempo)
      pausaStore.setBloquearTela(pausaSelecionada.value.bloquearTela)
      pausaStore.setTimer(getDiferencaEmSegundosParaADataAtual(horarioMarcacao.value))

      const tempoLimite = pausaSelecionada.value.tempo * 60

      pausaStore.setTimerInterval(setInterval(() => {
        pausaStore.setTimer(getDiferencaEmSegundosParaADataAtual(horarioMarcacao.value))

        if (tempoLimite && timer.value >= tempoLimite) {
          isAgenteAtrasado.value = true
        }
      }, 1000))
    }

    function resetPausa() {
      pausaEstaAtiva.value = false

      clearInterval(timerInterval.value)
      pausaStore.setTimer(0)
      pausaStore.setTimerInterval(null)

      isAgenteAtrasado.value = false
      pausaSelecionada.value = null
      pausaStore.$reset()
    }

    async function getPausas() {
      pausas.value = await PausasService.get(true)
    }

    async function continuarPausa() {
      try {
        pausaSelecionada.value = await PausasService.getById(pausaId.value)

        continuarTimer()
      } finally {
        loading.value = false
      }
    }

    async function iniciarPausa() {
      try {
        loading.value = true
        await PausasService.salvarRegistroPausaAgente(pausaSelecionada.value.id, { emPausa: true })
        iniciarTimer()
      } finally {
        loading.value = false
      }
    }

    async function finalizarPausa() {
      try {
        loading.value = true
        await PausasService.salvarRegistroPausaAgente(null, { emPausa: false })
        await resetPausa()
        EventBus.$emit('pausa-finalizada')
      } finally {
        loading.value = false
      }
    }

    async function handleOnSubmitBtnClick() {
      if (loading.value) return

      if (formRef.value.validate()) {
        await iniciarPausa()
      } else {
        Vue.$toast.warning('Preencha corretamente os campos antes de salvar')
      }
    }

    onMounted(getPausas)

    watch(
      () => props.isPausaDialogOpen,
      async value => {
        if (value && isAgenteEmPausa.value) {
          await continuarPausa()
        }
      },
      { immediate: true },
    )

    return {
      formRef,
      loading,
      closeDialog,
      pausaNome,
      bloquearTela,
      pausas,
      pausaSelecionada,
      pausaEstaAtiva,
      isAgenteAtrasado,
      mensagemTelaBloqueada,
      mensagemTelaNaoBloqueada,
      horarioFormatado,
      finalizarPausa,
      handleOnSubmitBtnClick,
      validators: {
        required,
        select,
      },
    }
  },
}
</script>
